import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';

import { shareProgramActions, shareProgramReducer } from './shareProgramReducer';
import ShareProgramContext from './ShareProgramContext';
import { fetchReferralInfo } from '../../api/storefront';
import { getCookie, removeCookie } from '../../util/cookies';
import useFeatureFlags from '../../hooks/use-feature-flags';

export default function ShareProgramProvider({ children }) {

  /* We need to know if we need the old referral or the new */
  const referralsFlag = (useFeatureFlags())['faster-friends'];

  const [state, dispatch] = useReducer(shareProgramReducer, {
    referralInfo: {},
    affiliateInfo: {},
    fetching: true,
    referralsFlag,
  });

  /**
   * FriendBuy data comes from their API that we load in via a
   * third party script. So we need to know when the script has
   * finished loading before we try to push further actions to
   * it's window object.
   */
  useEffect(() => {
    // Add a call to get the visitor status so we can get advocate data
    const getFBAdvocateOnLoad = () => {
      const existingReferral = getCookie('referral');
      if (existingReferral && existingReferral.includes('referrerId')) {
        dispatch({
          type: shareProgramActions.GET_REFERRAL_COOKIE,
          payload: existingReferral,
        });
        return;
      };

      const fbAPI = window.friendbuyAPI;
      fbAPI.push(["getVisitorStatus", (status) => {
        if (status.payload.advocate) {
          const { advocate } = status.payload;

          const fbUrlParameters = {
            fbuy: `${new URLSearchParams(window.location.search).get('fbuy')}`,
            fbshare: `${new URLSearchParams(window.location.search).get('fbshare')}`,
            referrerId: advocate.customerId ? `${advocate.customerId}`: null,
          };

          dispatch({ 
            type: shareProgramActions.API_INITIALIZE, 
            payload: {
              code: new URLSearchParams(window.location.search).get('referralCode'),
              referrerName: advocate.firstName,
              parameters: fbUrlParameters,
              /**
               * This is to help with a quick solution to the order page
               * Current referral uses this to show discounts, and fb is
               * only ever a referral. We'll remove this after the old program
               * is ended. Right now, this saves us from adding more feature
               * flag checks.
               */
              isReferral: true,
            },
          });
        } else {
          dispatch({
            type: shareProgramActions.API_INITIALIZE,
            payload: {
              checkAffiliate: true,
            },
          });
        }
      },]);
    }

    const pollFriendbuy = (remainingTrys) => {
      if (window.friendbuyAPI?.loaded) {
        getFBAdvocateOnLoad();
      } else if (remainingTrys > 0) {
        setTimeout(() => pollFriendbuy(remainingTrys - 1), 200);
      }
    }

    pollFriendbuy(25);
  }, []);

  const getGFReferralInfo = useCallback(async () => {
    if (!state.fetching) return;

    // If the flag is present, we don't want to make unneccessary API calls
    // If the API call isn't made, the old cookie is not helpful to the user
    if (state.referralsFlag ) {
      const referralCookie = getCookie('referral');

      // Want to ensure the cookie is not the new one
      // We also still need this because part of the redirect
      // includes the cookie already so we'll want to remove it.
      if (referralCookie && !referralCookie.includes('referrerId')) {
        removeCookie('referral');
      }
      return;
    }

    // Old referral only stores the code as it's cookie,
    // so unlike the fbuy cookie, we're going to do the call
    // everytime to the API because that's what returns the referral state info.

    // Grab `kid` from query params and pass through to referral Info.
    const kid = new URLSearchParams(window.location.search).get('kid');

    const payload = await fetchReferralInfo(kid);

    dispatch({
      type: shareProgramActions.API_INITIALIZE,
      payload,
    });
  }, [state]);
  
  // Referrals and affiliates have to stay separate for builder logic.
  return (
    <ShareProgramContext.Provider
      value={useMemo(() => ({
        referralInfo: state.referralInfo,
        affiliateInfo: state.fetching ? undefined : state.affiliateInfo,
        getGFReferralInfo,
      }), [state, getGFReferralInfo])}>
      {children}
    </ShareProgramContext.Provider>
  )
}

ShareProgramProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
